/**
 * SF-105: shared newbie-protection helpers. Single source of truth for "is this province still
 * protected" and its countdown copy - mirrors fuzzyValue.ts's pattern (don't copy-paste into
 * components).
 *
 * All backend surfaces derive protection status the same way:
 * protectionExpiresAtTick > currentTick (never absence-checking - the field is non-nullable on
 * the backend, but callers here treat a missing/non-finite value as "not protected" per the
 * project's Redis-cached-DTO-field convention).
 */

/** True iff the province is still within its protection window. */
export function isProtected(protectionExpiresAtTick: number | undefined, currentTick: number): boolean {
  if (!Number.isFinite(protectionExpiresAtTick)) {
    return false;
  }
  return (protectionExpiresAtTick as number) > currentTick;
}

/**
 * Relative countdown copy for an active protection window, e.g. "Protected for 3 more days".
 * 1 tick = 1 Utopian day (docs/TIME.md) - user-facing vocabulary must say "days", not "ticks".
 * Returns null when not protected.
 */
export function formatProtectionCountdown(
  protectionExpiresAtTick: number | undefined,
  currentTick: number
): string | null {
  if (!isProtected(protectionExpiresAtTick, currentTick)) {
    return null;
  }
  const daysRemaining = (protectionExpiresAtTick as number) - currentTick;
  return `Protected for ${daysRemaining} more ${daysRemaining === 1 ? 'day' : 'days'}`;
}
